import { computed, unref } from 'vue';

import { useDashboards } from '@/app/dashboards/composables/useDashboards';
import { REPORT_CATEGORY_OPTIONS } from '@/app/dashboards/utils/reports.enum';

export function useReportCategory({ reportCategory } = {}) {
  const { canAccessStatsEngineV2 } = useDashboards();
  const category = computed(() => unref(reportCategory));

  const categoryConfig = computed(() =>
    Object.values(REPORT_CATEGORY_OPTIONS).find((config) => config.id === category.value),
  );

  const isCommunityReportCategory = computed(
    () => category.value === REPORT_CATEGORY_OPTIONS.COMMUNITY.id || false,
  );

  const isEarnedReportCategory = computed(
    () => category.value === REPORT_CATEGORY_OPTIONS.EARNED.id || false,
  );

  const isIndustryReportCategory = computed(
    () => category.value === REPORT_CATEGORY_OPTIONS.INDUSTRY.id || false,
  );

  const isListeningReportCategory = computed(
    () => category.value === REPORT_CATEGORY_OPTIONS.SOCIAL_LISTENING.id || false,
  );

  const isTSIReportCategory = computed(
    () => category.value === REPORT_CATEGORY_OPTIONS.TSI.id || false,
  );

  const isCompetitiveCategory = computed(
    () => category.value === REPORT_CATEGORY_OPTIONS.COMPETITIVE.id || false,
  );

  const isOwnedReportCategory = computed(() => category.value === REPORT_CATEGORY_OPTIONS.OWNED.id);

  const isAdsReportCategory = computed(
    () => category.value === REPORT_CATEGORY_OPTIONS.SOCIAL_ADVERTISING.id,
  );

  const hasLockedMetric = computed(() => !!categoryConfig.value?.lockedMetric);

  const hasLockedChannels = computed(() => !!categoryConfig.value?.lockedChannels);

  // Whether multi-brand + multi-channel line charts are supported for this
  // report category. Stats engine v2 supports this for owned reports today;
  // earned (sc-189783), competitive (sc-189788), and ads (sc-189793) are
  // tracked separately. Once those land, this flag (and the call sites that
  // gate on it) can be removed.
  const lineChartSupported = computed(
    () => canAccessStatsEngineV2.value && isOwnedReportCategory.value,
  );

  return {
    categoryConfig,
    isAdsReportCategory,
    isCommunityReportCategory,
    isCompetitiveCategory,
    isEarnedReportCategory,
    isIndustryReportCategory,
    isListeningReportCategory,
    isOwnedReportCategory,
    isTSIReportCategory,
    hasLockedMetric,
    hasLockedChannels,
    lineChartSupported,
  };
}
